// ============================================================
// CHANNEL MONITOR — Fase 12.1
// Rastreia canais Supabase abertos/fechados
// Detecta: channels zumbis, reconexões excessivas, leaks
// ============================================================
import { supabase } from '@/lib/supabase'

export interface ChannelRecord {
  name:      string
  openedAt:  number
  closedAt?: number
  topic:     string
}

const _open:   Map<string, ChannelRecord> = new Map()
const _closed: ChannelRecord[]            = []
const MAX_CLOSED_HISTORY = 200

// ─── Track open/close ─────────────────────────────────────
export function trackChannelOpen(name: string, topic: string): void {
  _open.set(name, { name, openedAt: Date.now(), topic })
}

export function trackChannelClose(name: string): void {
  const rec = _open.get(name)
  if (rec) {
    const closed = { ...rec, closedAt: Date.now() }
    _closed.unshift(closed)
    if (_closed.length > MAX_CLOSED_HISTORY) _closed.pop()
    _open.delete(name)
  }
}

// ─── Native channel wrapper ───────────────────────────────
// Wraps supabase.channel() to auto-track open/close
export function trackedChannel(name: string, topic = name) {
  trackChannelOpen(name, topic)
  const ch = supabase.channel(name)
  const original = ch.unsubscribe.bind(ch)
  ch.unsubscribe = async () => {
    trackChannelClose(name)
    return original()
  }
  return ch
}

// ─── Snapshot ─────────────────────────────────────────────
export interface ChannelSnapshot {
  openCount:      number
  closedCount:    number
  openChannels:   ChannelRecord[]
  zombies:        ChannelRecord[]   // open > 10min
  recentClosed:   ChannelRecord[]
}

export function getChannelSnapshot(): ChannelSnapshot {
  const now  = Date.now()
  const open = Array.from(_open.values())
  return {
    openCount:    open.length,
    closedCount:  _closed.length,
    openChannels: open,
    zombies:      open.filter(c => now - c.openedAt > 10 * 60_000),
    recentClosed: _closed.slice(0, 20),
  }
}

export function getOpenChannelCount(): number {
  return _open.size
}
